import * as React from "react"

import type { ToastActionElement, ToastProps } from "@/components/ui/toast"

/**
 * What a toast is saying, which decides its mark (and, for an error, its
 * surface). PropPay's four (J6, 2026-09-28):
 *
 *   success     — the thing you asked for happened
 *   default     — information: nothing went wrong, nothing to celebrate
 *   warning     — it happened, or might have, but needs your attention
 *   destructive — it did not happen
 */
export type ToastVariant = "default" | "success" | "warning" | "destructive"

/** Never more than this many on screen; the oldest gives way to the newest. */
export const TOAST_LIMIT = 3

/** How long a closed toast stays mounted so its exit can play. */
export const TOAST_REMOVE_DELAY = 1000

/**
 * How long each kind stays up. A problem is read more slowly than a
 * confirmation and usually has to be acted on, so it stays longer. A caller's
 * own `duration` wins; `Infinity` keeps it until the reader acts.
 */
export const TOAST_DURATION: Record<ToastVariant, number> = {
  success: 4500,
  default: 5500,
  warning: 8000,
  destructive: 8000,
}

/** A toast's one action: an element, or just a label and what it does. */
export type ToastActionSpec = ToastActionElement | { label: string; onClick: () => void }

type ToasterToast = Omit<ToastProps, "title"> & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionSpec
  variant?: ToastVariant
  /**
   * Called once when the toast closes by itself or is closed by the reader
   * (✕, swipe, Escape, timeout) — so a nudge can remember it was declined.
   * Not called when a newer copy of the same notice replaces it.
   */
  onDismiss?: () => void
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

/**
 * The same notice twice: same kind, same words. Only plain text is compared —
 * a notice built from elements cannot be told apart from another, so it is
 * never merged.
 */
function sameNotice(a: ToasterToast, b: ToasterToast): boolean {
  const text = (v: React.ReactNode) => v === undefined || typeof v === "string"
  if (!text(a.title) || !text(a.description) || !text(b.title) || !text(b.description)) return false
  if (a.title === undefined && a.description === undefined) return false
  return (
    a.title === b.title &&
    a.description === b.description &&
    (a.variant ?? "default") === (b.variant ?? "default")
  )
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST": {
      const incoming = action.toast
      // THE SAME NOTICE RAISED AGAIN (a double click, a retried request) is
      // one notice: the fresh copy takes the old one's place — same slot, a
      // new timer — instead of stacking a duplicate under it.
      const dupe = state.toasts.findIndex((t) => t.open !== false && sameNotice(t, incoming))
      const toasts =
        dupe === -1
          ? [...state.toasts, incoming]
          : state.toasts.map((t, i) => (i === dupe ? incoming : t))

      // At most TOAST_LIMIT on screen. The oldest open ones are closed (so
      // they still animate out) rather than cut from the list.
      let excess = toasts.filter((t) => t.open !== false).length - TOAST_LIMIT
      return {
        ...state,
        toasts: toasts.map((t) => {
          if (excess > 0 && t.open !== false && t !== incoming) {
            excess--
            return { ...t, open: false }
          }
          return t
        }),
      }
    }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  // Anything now closed leaves the list once its exit has played. Swept here,
  // after the reducer, so the reducer stays pure.
  memoryState.toasts.forEach((t) => {
    if (t.open === false) addToRemoveQueue(t.id)
  })
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  const id = genId()
  const variant: ToastVariant = props.variant ?? "default"
  let closed = false

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      variant,
      duration: props.duration ?? TOAST_DURATION[variant],
      open: true,
      onOpenChange: (open) => {
        if (open) return
        if (!closed) {
          closed = true
          props.onDismiss?.()
        }
        dismiss()
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

/** Close one toast (or all of them). */
function dismissToast(toastId?: string) {
  dispatch({ type: "DISMISS_TOAST", toastId })
}

/** What is queued right now, outside React — for tests and diagnostics. */
function getToasts(): readonly ToasterToast[] {
  return memoryState.toasts
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    // A TOAST FIRED DURING THE SAME COMMIT USED TO BE SWALLOWED.
    //
    // `useState(memoryState)` snapshots at RENDER time, and `dispatch` only
    // notifies listeners already in the array — but this effect is what adds
    // the listener, and effects run children-and-earlier-siblings first. So a
    // component that toasts from its own mount effect dispatched into a
    // `listeners` array that did not yet contain this Toaster, while this
    // Toaster's `state` was captured before the dispatch. The toast landed in
    // `memoryState` and was never rendered by anyone.
    //
    // Observed on the login page's involuntary-sign-out notice (Login.tsx),
    // which is exactly that shape and only on a COLD LOAD: an SPA navigation
    // leaves the Toaster mounted and subscribed, so the same code worked in
    // dev and vanished on the full-page redirect that real sign-outs take.
    //
    // Re-reading on subscribe closes the window. When nothing was missed this
    // is a no-op — `state` is already the same object React just stored.
    setState(memoryState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: dismissToast,
  }
}

export { useToast, toast, dismissToast, getToasts }
